import { useCallback, useEffect } from 'react';
import { useNoticeBannersStackContext } from '@sg/garnish';

// ────────────────────────────────────────────────────────────────────────────────

export const useCartMutationErrorNotice = (
  responseData: LineItemErrorResponseData,
) => {
  const errorMessage = responseData?.message;
  const responseType = responseData?.__typename;

  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── ERRORS ──────────────────────────────────────────────────────

  const isRestaurantMaxQuantityExceeded =
    responseType === 'RestaurantMaxQuantityExceeded';
  const isRestaurantMaxDeliveryQuantityExceeded =
    responseType === 'RestaurantMaxDeliveryQuantityExceeded';
  const isMaxQuantityErrorOccurred =
    isRestaurantMaxQuantityExceeded || isRestaurantMaxDeliveryQuantityExceeded;
  const isMaxModificationLimitExceeded =
    responseType === 'MaxModificationsExceeded';
  const hasValidationErrors = responseType === 'ValidationError';

  // ─── HELPERS ─────────────────────────────────────────────────────

  const handleRestaurantMaxQuantityError = useCallback(() => {
    if (!errorMessage) return;

    addNoticeBanner({ text: errorMessage, palette: 'caution' });
  }, [errorMessage, addNoticeBanner]);

  const handleMaxModificationLimitExceededError = useCallback(() => {
    if (!errorMessage) return;

    addNoticeBanner({ text: errorMessage, palette: 'neutral' });
  }, [errorMessage, addNoticeBanner]);

  const handleValidationErrors = useCallback(() => {
    const fieldErrorMessage = responseData?.fieldErrors?.[0]?.message;

    if (!fieldErrorMessage) return;

    addNoticeBanner({ text: fieldErrorMessage, palette: 'caution' });
  }, [responseData, addNoticeBanner]);

  // ─── EFFECTS ─────────────────────────────────────────────────────

  useEffect(() => {
    if (isMaxQuantityErrorOccurred) {
      handleRestaurantMaxQuantityError();
    }

    if (isMaxModificationLimitExceeded) {
      handleMaxModificationLimitExceededError();
    }

    if (hasValidationErrors) {
      handleValidationErrors();
    }
  }, [
    addNoticeBanner,
    responseData,
    isMaxModificationLimitExceeded,
    isRestaurantMaxDeliveryQuantityExceeded,
    isRestaurantMaxQuantityExceeded,
    hasValidationErrors,
    isMaxQuantityErrorOccurred,
    handleRestaurantMaxQuantityError,
    handleMaxModificationLimitExceededError,
    handleValidationErrors,
  ]);
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type LineItemErrorResponseData =
  | {
      readonly __typename?: string;
      readonly message?: string;
      readonly fieldErrors?: ReadonlyArray<{
        readonly __typename?: 'FieldValidationError';
        readonly field: string;
        readonly message: string;
      }>;
    }
  | undefined;
